import { router } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import ConfirmModal from '../../components/shared/ConfirmModal'

type RevisionId = number | string
interface TemplateRevision {
  id: RevisionId
  content: Record<string, string>
  design?: string
  created_at: string
}
export interface EmailTemplateFamily {
  key: string
  label: string
  fields: Record<string, string>
  tags: Record<string, string>
  scenarios: { key: string; label: string }[]
  draft: TemplateRevision | null
  published: TemplateRevision | null
  revisions: { id: RevisionId; created_at: string }[]
}
interface Preview {
  subject: string
  html: string
  text: string
  external_warning: string | null
}
interface EditState {
  content: Record<string, string>
  baseline: Record<string, string>
  draftId: RevisionId | null
  design: string
  baselineDesign: string
}

const INPUT = 'w-full px-3 py-2 border border-stone/30 rounded-lg text-sm'
const SECONDARY = 'px-3 py-2 border border-stone/40 rounded-lg text-xs font-semibold text-text-primary hover:bg-[#F5F5F5] disabled:opacity-50'
const PRIMARY = 'px-4 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold hover:bg-[#C7161E] disabled:opacity-50'
const sameContent = (a: Record<string, string>, b: Record<string, string>) =>
  Object.keys({ ...a, ...b }).every(key => a[key] === b[key])
const initialEdit = (family: EmailTemplateFamily): EditState => {
  const content = { ...family.fields, ...(family.draft?.content ?? family.published?.content ?? {}) }
  const design = family.draft?.design ?? family.published?.design ?? 'legacy'
  return { content, baseline: { ...content }, draftId: family.draft?.id ?? null, design, baselineDesign: design }
}
const newToken = () => typeof crypto !== 'undefined' && 'randomUUID' in crypto
  ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`

// Defense in depth: no scripts, same-origin privileges, forms, navigation or
// remote resources except the two fixed, app-hosted brand assets.
// Strip active elements before adding the leading CSP.
function sandboxDocument(html: string) {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  doc.querySelectorAll('script,iframe,object,embed,base,meta,link,form').forEach(node => node.remove())
  doc.querySelectorAll('*').forEach(node => {
    Array.from(node.attributes).forEach(attribute => {
      if (/^on/i.test(attribute.name) || ['href', 'action', 'formaction', 'srcdoc'].includes(attribute.name.toLowerCase())) {
        node.removeAttribute(attribute.name)
      }
    })
  })
  const brandPaths = ['/pinnacle-logo.png', '/images/pinnacle-wordmark-white.png']
  doc.querySelectorAll('img').forEach(image => {
    const path = new URL(image.getAttribute('src') ?? '', window.location.origin).pathname
    if (!brandPaths.includes(path)) { image.remove(); return }
    image.removeAttribute('srcset')
    image.src = `${window.location.origin}${path}`
  })
  const csp = doc.createElement('meta')
  csp.httpEquiv = 'Content-Security-Policy'
  csp.content = `default-src 'none'; script-src 'none'; style-src 'unsafe-inline'; img-src ${brandPaths.map(path => `${window.location.origin}${path}`).join(' ')}; font-src 'none'; connect-src 'none'; frame-src 'none'; object-src 'none'; base-uri 'none'; form-action 'none'`
  doc.head.prepend(csp)
  return `<!doctype html>${doc.documentElement.outerHTML}`
}

export default function EmailTemplateEditor({
  families, superAdminEmail, busy, unsaved, operatorTestsAllowed, batchId,
}: {
  families: EmailTemplateFamily[]
  superAdminEmail: string | null
  busy: boolean
  unsaved: boolean
  operatorTestsAllowed: boolean
  batchId: number | null
}) {
  const [familyKey, setFamilyKey] = useState(families[0]?.key ?? '')
  const [edits, setEdits] = useState<Record<string, EditState>>(() =>
    Object.fromEntries(families.map(family => [family.key, initialEdit(family)])))
  const [scenarioKey, setScenarioKey] = useState('')
  const [previewSource, setPreviewSource] = useState('draft')
  const [preview, setPreview] = useState<(Preview & { label: string; document: string }) | null>(null)
  const [previewBusy, setPreviewBusy] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [working, setWorking] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [recipient, setRecipient] = useState(superAdminEmail ?? '')
  const [tag, setTag] = useState('')
  const [focusedField, setFocusedField] = useState<string | null>(null)
  const [pendingSwitch, setPendingSwitch] = useState<string | null>(null)
  const [confirmation, setConfirmation] = useState<{ action: 'publish' | 'restore' | 'send'; id: RevisionId } | null>(null)
  const focused = useRef<{ key: string; start: number; end: number } | null>(null)
  const pendingCaret = useRef<{ key: string; position: number } | null>(null)
  const inputs = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | null>>({})
  const mounted = useRef(true)
  const request = useRef(0)
  const controller = useRef<AbortController | null>(null)
  const posting = useRef(false)
  const sendIntent = useRef<{ key: string; token: string } | null>(null)

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false; request.current += 1; controller.current?.abort() }
  }, [])

  useEffect(() => {
    const caret = pendingCaret.current
    if (!caret) return
    pendingCaret.current = null
    inputs.current[caret.key]?.focus()
    inputs.current[caret.key]?.setSelectionRange(caret.position, caret.position)
  }, [edits])

  // Polling must not overwrite local work. Clean families follow server props;
  // saves explicitly reconcile their returned revision below.
  useEffect(() => {
    setEdits(previous => {
      const next = { ...previous }
      families.forEach(family => {
        const current = previous[family.key]
        if (!current || (sameContent(current.content, current.baseline) && current.design === current.baselineDesign)) next[family.key] = initialEdit(family)
      })
      return next
    })
    if (!familyKey && families[0]) setFamilyKey(families[0].key)
  }, [families, familyKey])

  const family = families.find(item => item.key === familyKey)
  const edit = family ? edits[family.key] ?? initialEdit(family) : null
  const dirty = Boolean(edit && (!sameContent(edit.content, edit.baseline) || edit.design !== edit.baselineDesign))
  const scenario = family?.scenarios.find(item => item.key === scenarioKey) ?? family?.scenarios[0]
  const locked = busy || working !== null
  const draftId = edit?.draftId ?? null
  const canSend = !locked && !dirty && !unsaved && operatorTestsAllowed && draftId !== null
    && Boolean(scenario) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.trim())

  const clearPreview = () => {
    request.current += 1
    controller.current?.abort()
    setPreview(null)
    setPreviewError(null)
    setPreviewBusy(false)
  }
  const switchFamily = (key: string) => {
    setFamilyKey(key)
    setScenarioKey('')
    setTag('')
    focused.current = null
    setFocusedField(null)
    setErrors({})
    setNotice(null)
    setConfirmation(null)
    clearPreview()
  }
  const changeField = (key: string, value: string) => {
    if (!family || !edit) return
    setEdits(previous => ({ ...previous, [family.key]: { ...edit, content: { ...edit.content, [key]: value } } }))
    setNotice(null)
    setConfirmation(null)
    clearPreview()
  }
  const insertTag = () => {
    if (!edit || !tag || !focused.current) return
    const { key, start, end } = focused.current
    const value = edit.content[key] ?? ''
    const insertion = tag.includes('{{') ? tag : `{{${tag}}}`
    changeField(key, value.slice(0, start) + insertion + value.slice(end))
    const position = start + insertion.length
    focused.current = { key, start: position, end: position }
    pendingCaret.current = { key, position }
  }

  const post = (action: 'save' | 'publish' | 'restore' | 'send', revisionId?: RevisionId) => {
    if (!family || !edit || locked || posting.current) return
    if (action !== 'save' && dirty) return
    if (action === 'send' && !canSend) return
    const submittedFamily = family.key
    const submittedContent = { ...edit.content }
    const submittedDesign = edit.design
    const data = action === 'save'
      ? { family: family.key, content: submittedContent, design: edit.design, expected_draft_id: draftId, batch_id: batchId }
      : action === 'send'
        ? { recipient: recipient.trim(), catalog_keys: [scenario!.key], template_revision_id: revisionId, submission_token: '', batch_id: batchId }
        : action === 'restore'
          ? { family: family.key, revision_id: revisionId, expected_draft_id: draftId, batch_id: batchId }
          : { family: family.key, revision_id: revisionId, batch_id: batchId }
    if (action === 'send') {
      const intentKey = JSON.stringify([family.key, revisionId, scenario!.key, recipient.trim()])
      if (sendIntent.current?.key !== intentKey) sendIntent.current = { key: intentKey, token: newToken() }
      Object.assign(data, { submission_token: sendIntent.current.token })
    }
    posting.current = true
    setWorking(action)
    setErrors({})
    setNotice(null)
    router.post(`/super_admin/email_setting/${action === 'send' ? 'send_tests' : `${action}_template`}`, data, {
      preserveScroll: true,
      preserveState: true,
      onError: validation => { if (mounted.current) setErrors(validation) },
      onSuccess: page => {
        if (!mounted.current) return
        const returned = (page.props.template_families as EmailTemplateFamily[] | undefined)?.find(item => item.key === submittedFamily)
        if ((action === 'save' || action === 'restore' || action === 'publish') && returned) {
          setEdits(previous => {
            const current = previous[submittedFamily]
            const saved = initialEdit(returned)
            // Never erase edits made after the submitted snapshot.
            if (current && (!sameContent(current.content, submittedContent) || current.design !== submittedDesign)) {
              return { ...previous, [submittedFamily]: { ...current, baseline: saved.baseline, baselineDesign: saved.baselineDesign, draftId: saved.draftId } }
            }
            return { ...previous, [submittedFamily]: saved }
          })
        }
        setConfirmation(null)
        clearPreview()
        setNotice(action === 'save' ? 'Draft saved. Preview or send this exact saved revision before publishing.'
          : action === 'restore' ? 'Previous published content restored as a new draft. Review before publishing.'
            : action === 'publish' ? 'Saved draft published.'
              : 'Sample requested. Check the test panel below for queue and delivery results; acceptance is not proof of inbox placement.')
      },
      onFinish: () => { posting.current = false; if (mounted.current) setWorking(null) },
    })
  }

  const loadPreview = async () => {
    if (!family || !scenario || locked || dirty || previewBusy || (previewSource === 'draft' && draftId === null)) return
    clearPreview()
    const sequence = ++request.current
    const abort = new AbortController()
    controller.current = abort
    const params = new URLSearchParams({ family: family.key, scenario: scenario.key })
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.trim())) params.set('recipient', recipient.trim())
    if (previewSource === 'draft') params.set('revision_id', String(draftId))
    setPreviewBusy(true)
    try {
      const response = await fetch(`/super_admin/email_setting/template_preview?${params}`, { // allow-direct-fetch: read-only JSON preview; mutations use Inertia
        credentials: 'include', headers: { Accept: 'application/json' }, signal: abort.signal,
      })
      const result: (Preview & { error?: string }) | null = await response.json().catch(() => null)
      if (!response.ok || (typeof result?.error === 'string' && result.error)) {
        throw new Error(typeof result?.error === 'string' && result.error
          ? `${result.error} Check the saved revision and selected scenario, then retry preview.`
          : 'Preview could not be loaded. Check the saved revision and selected scenario, then retry preview.')
      }
      if (!result || typeof result.subject !== 'string' || typeof result.html !== 'string' || typeof result.text !== 'string') {
        throw new Error('The preview response was invalid. Please try again.')
      }
      if (mounted.current && sequence === request.current) setPreview({
        ...result, document: sandboxDocument(result.html),
        label: `${scenario.label} · ${previewSource === 'draft' ? `Saved draft #${draftId}` : family.published ? `Published #${family.published.id}` : 'Built-in default'}`,
      })
    } catch (error) {
      if (mounted.current && sequence === request.current && !abort.signal.aborted) {
        setPreviewError(error instanceof Error ? error.message : 'Preview unavailable. Please try again.')
      }
    } finally {
      if (mounted.current && sequence === request.current) setPreviewBusy(false)
    }
  }

  if (!family || !edit) return (
    <div className="bg-white border border-stone/20 rounded-lg p-6">
      <h2 className="text-base font-semibold text-text-primary mb-1">Email templates</h2>
      <p className="text-xs text-text-muted">Template editing is not available yet. Provider settings and outbound tests remain available below.</p>
    </div>
  )

  return (
    <div className="bg-white border border-stone/20 rounded-lg p-6">
      <h2 className="text-base font-semibold text-text-primary mb-1">Email templates</h2>
      <p className="text-xs text-text-muted mb-4">Save a draft, preview and test its exact revision, then explicitly publish. Saving or restoring a draft does not change customer email.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label htmlFor="template-family" className="block text-xs font-semibold text-text-primary mb-1">Template family</label>
          <select id="template-family" className={INPUT} value={family.key} disabled={locked}
            onChange={e => dirty ? setPendingSwitch(e.target.value) : switchFamily(e.target.value)}>
            {families.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="template-scenario" className="block text-xs font-semibold text-text-primary mb-1">Sample scenario</label>
          <select id="template-scenario" className={INPUT} value={scenario?.key ?? ''} disabled={locked || previewBusy || !scenario}
            onChange={e => { setScenarioKey(e.target.value); setConfirmation(null); clearPreview() }}>
            {!scenario && <option value="">No scenarios available</option>}
            {family.scenarios.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
          </select>
        </div>
      </div>
      <div className="border border-stone/20 rounded-lg p-3 mb-4 text-xs text-text-muted space-y-1">
        <p><strong className="text-text-primary">Published:</strong> {family.published ? `Revision #${family.published.id} · ${family.published.created_at}` : 'Built-in default; no custom revision published.'}</p>
        <p><strong className="text-text-primary">Draft:</strong> {draftId !== null ? `Revision #${draftId}${family.draft ? ` · ${family.draft.created_at}` : ''}` : 'No saved draft.'} {dirty && 'Unsaved edits — save before previewing, sending or publishing.'}</p>
      </div>
      <div className="text-xs text-text-muted mb-4 space-y-1">
        <p>Protected actions, authentication and invitation links are generated by Pinnacle and cannot be replaced here. Sample action links are inert; tests do not invite people, issue reset tokens or change billing or meeting state.</p>
        <p>Only the supported fields below are editable. Optional content is shown only when the scenario supplies its data; tags do not create missing data.</p>
        <p>Recap sections, order and meeting data remain controlled by Pinnacle. An external SendGrid recap template can override the final subject and markup; a local preview cannot verify that external template.</p>
      </div>
      {Object.keys(errors).length > 0 && <div role="alert" className="text-xs text-[#ED1C24] mb-4">{Object.entries(errors).map(([key, message]) => <p key={key}>{message}</p>)}</div>}
      {notice && <p role="status" className="text-sm text-text-primary mb-4">{notice}</p>}
      <div className="space-y-4">
        <div>
          <label htmlFor="template-design" className="block text-sm font-semibold text-text-primary mb-1">Email design</label>
          <select id="template-design" className={INPUT} value={edit.design} disabled={locked}
            onChange={event => {
              setEdits(previous => ({ ...previous, [family.key]: { ...edit, design: event.target.value } }))
              setNotice(null)
              setConfirmation(null)
              clearPreview()
            }}>
            <option value="structured_card">Approved — light structured-card</option>
            <option value="legacy">Legacy design (rollback)</option>
          </select>
          <p className="text-xs text-text-muted mt-1">Design and copy are saved together. Customer emails change only when you publish the saved draft. Delivery mode is unchanged.</p>
        </div>
        {Object.keys(family.fields).map(key => {
          const label = key.replace(/_/g, ' ').replace(/^./, char => char.toUpperCase())
          const focus = (element: HTMLInputElement | HTMLTextAreaElement) => {
            focused.current = { key, start: element.selectionStart ?? 0, end: element.selectionEnd ?? 0 }
            setFocusedField(key)
          }
          return <div key={key}>
            <label htmlFor={`template-field-${key}`} className="block text-sm font-semibold text-text-primary mb-1">{label}</label>
            {key === 'subject' ? (
              <input id={`template-field-${key}`} type="text" className={INPUT} value={edit.content[key] ?? ''} disabled={locked}
                ref={node => { inputs.current[key] = node }} onFocus={e => focus(e.currentTarget)} onSelect={e => focus(e.currentTarget)}
                onChange={e => changeField(key, e.target.value)} />
            ) : (
              <textarea id={`template-field-${key}`} rows={4} className={INPUT} value={edit.content[key] ?? ''} disabled={locked}
                ref={node => { inputs.current[key] = node }} onFocus={e => focus(e.currentTarget)} onSelect={e => focus(e.currentTarget)}
                onChange={e => changeField(key, e.target.value)} />
            )}
          </div>
        })}
      </div>
      <div className="mt-4">
        <label htmlFor="template-tag" className="block text-xs font-semibold text-text-primary mb-1">Available tags for {family.label}</label>
        <div className="flex items-start gap-2">
          <select id="template-tag" value={tag} onChange={e => setTag(e.target.value)} disabled={locked} className={INPUT}>
            <option value="">Choose a tag</option>
            {Object.entries(family.tags).map(([key]) => <option key={key} value={key}>{key}</option>)}
          </select>
          <button type="button" className={`${SECONDARY} whitespace-nowrap`} disabled={locked || !tag || !focusedField} onClick={insertTag}>Insert tag</button>
        </div>
        <p className="text-xs text-text-muted mt-1">{tag ? family.tags[tag] : 'Choose a tag to see its meaning. Click a field first; insertion replaces its selected text at the cursor.'}</p>
      </div>
      <div className="flex flex-wrap justify-end gap-2 mt-5 pt-4 border-t border-stone/10">
        <button type="button" className={PRIMARY} disabled={locked || (!dirty && draftId !== null)} onClick={() => post('save')}>{working === 'save' ? 'Saving…' : 'Save draft'}</button>
        <button type="button" className={SECONDARY} disabled={locked || dirty || draftId === null || draftId === family.published?.id}
          onClick={() => setConfirmation({ action: 'publish', id: draftId! })}>Publish saved draft</button>
      </div>
      <div className="mt-5 pt-4 border-t border-stone/10">
        <label htmlFor="template-preview-source" className="block text-xs font-semibold text-text-primary mb-1">Preview source</label>
        <div className="flex flex-wrap gap-2">
          <select id="template-preview-source" className={`${INPUT} sm:w-auto`} value={previewSource} disabled={locked || previewBusy}
            onChange={e => { setPreviewSource(e.target.value); clearPreview() }}>
            <option value="draft">Exact saved draft</option>
            <option value="published">Published / built-in default</option>
          </select>
          <button type="button" className={SECONDARY} disabled={locked || dirty || previewBusy || !scenario || (previewSource === 'draft' && draftId === null)} onClick={loadPreview}>
            {previewBusy ? 'Loading preview…' : previewError ? 'Retry preview' : 'Load preview'}
          </button>
        </div>
        <p className="text-xs text-text-muted mt-1">Unsaved editor content is never previewed or sent. Images, links and active content are disabled in the preview.</p>
        {previewBusy && <div role="status" aria-label="Loading preview" className="mt-3 border border-stone/20 rounded-lg p-4 space-y-3"><div className="h-4 w-2/3 bg-[#F5F5F5] rounded" /><div className="h-32 bg-[#F5F5F5] rounded" /></div>}
        {previewError && <p role="alert" className="text-xs text-[#ED1C24] mt-3">{previewError}</p>}
        {preview && <div className="mt-4 space-y-3">
          <p className="text-xs font-semibold text-text-primary">{preview.label}</p>
          <p className="text-sm text-text-primary break-words"><strong>Subject:</strong> {preview.subject}</p>
          {preview.external_warning && <p role="status" className="border border-[#F5C518]/40 bg-[#FFF8E1] rounded-lg p-3 text-xs text-[#5C4A1A]">{preview.external_warning}</p>}
          <iframe title="Saved email HTML preview" sandbox="" referrerPolicy="no-referrer" srcDoc={preview.document} className="w-full border border-stone/20 rounded-lg h-96" />
          <h3 className="text-xs font-semibold text-text-primary">Plain-text preview</h3>
          <pre className="text-xs text-text-primary whitespace-pre-wrap break-words bg-[#F5F5F5] rounded-lg p-3 max-h-96 overflow-auto">{preview.text}</pre>
        </div>}
      </div>
      <div className="mt-5 pt-4 border-t border-stone/10">
        <label htmlFor="template-test-inbox" className="block text-xs font-semibold text-text-primary mb-1">Send this saved draft to one test inbox</label>
        <input id="template-test-inbox" type="email" className={INPUT} value={recipient} disabled={locked}
          onChange={e => { setRecipient(e.target.value); setConfirmation(null); clearPreview() }} />
        <p className="text-xs text-text-muted mt-1">Real email to this chosen inbox only, using fictional sample data and inert action links. Nobody is created or contacted as a customer. Results appear in the existing test panel below.</p>
        {!operatorTestsAllowed && <p className="text-xs text-[#ED1C24] mt-2">Save a configured provider and select Test only or Live before sending. Off sends nothing.</p>}
        {unsaved && <p className="text-sm text-text-primary mt-2">Save provider configuration changes before sending a test.</p>}
        <button type="button" className={`${PRIMARY} mt-3`} disabled={!canSend} onClick={() => setConfirmation({ action: 'send', id: draftId! })}>
          {working === 'send' ? 'Queueing…' : 'Send exact saved draft'}
        </button>
      </div>
      <div className="mt-5 pt-4 border-t border-stone/10">
        <h3 className="text-sm font-semibold text-text-primary mb-1">Published revision history</h3>
        <p className="text-xs text-text-muted mb-3">Restore copies a published revision into a new draft for review, replacing any current saved draft. The old revision remains in immutable storage; published customer content does not change. Save unsaved edits before restoring.</p>
        {family.revisions.length === 0 ? <p className="text-xs text-text-muted">No published revisions yet.</p> : (
          <ul className="divide-y divide-stone/10 border border-stone/15 rounded-lg">
            {family.revisions.map(revision => <li key={revision.id} className="p-3 flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-text-muted">Revision #{revision.id} · {revision.created_at}{revision.id === family.published?.id ? ' · Currently published' : ''}</span>
              <button type="button" className={SECONDARY} disabled={locked || dirty} onClick={() => setConfirmation({ action: 'restore', id: revision.id })}>Restore as draft</button>
            </li>)}
          </ul>
        )}
      </div>
      <ConfirmModal isOpen={pendingSwitch !== null} title="Discard unsaved template edits?" variant="warning"
        message="Switching template families will discard the unsaved edits in this family. Saved drafts and published revisions will not change."
        confirmLabel="Discard and switch" submitting={locked} onClose={() => setPendingSwitch(null)}
        onConfirm={() => {
          if (locked || pendingSwitch === null) return
          setEdits(previous => ({ ...previous, [family.key]: initialEdit(family) }))
          switchFamily(pendingSwitch)
          setPendingSwitch(null)
        }} />
      <ConfirmModal isOpen={confirmation !== null} variant="warning" submitting={locked}
        title={confirmation?.action === 'publish' ? 'Publish saved draft?' : confirmation?.action === 'restore' ? 'Restore revision as a new draft?' : 'Send exact saved draft?'}
        message={confirmation?.action === 'publish'
          ? `Publish revision #${confirmation.id} for ${family.label}. Future customer email will use this content; external SendGrid templates may still override recap markup.`
          : confirmation?.action === 'restore'
            ? `Copy revision #${confirmation.id} into a new draft for ${family.label}.${draftId !== null ? ` This will replace the current saved draft #${draftId}; its immutable revision remains stored.` : ''} Published customer content will not change. If another operator changes the saved draft first, restore will be rejected; review the latest draft before retrying.`
            : `Queue one sample of revision #${confirmation?.id} (${scenario?.label}) to ${recipient.trim()} only. SendGrid acceptance does not confirm delivery or inbox placement.`}
        confirmLabel={confirmation?.action === 'publish' ? 'Publish revision' : confirmation?.action === 'restore' ? 'Restore as draft' : 'Queue sample'}
        onClose={() => setConfirmation(null)} onConfirm={() => { if (confirmation) post(confirmation.action, confirmation.id) }} />
    </div>
  )
}